// @flow

// Libs
import React, { Component } from 'react'
import _delay from 'lodash/delay'
import _size from 'lodash/size'
import _trim from 'lodash/trim'
import _toUpper from 'lodash/toUpper'
import _find from 'lodash/find'
import _pick from 'lodash/pick'
import { FormattedMessage } from 'react-intl'
import messages from '../messages'

// Types
import type { Option } from 'hfty/types'
import type { AccountOperation } from 'hfty-operator-accounts'

// Components
import Textfield from '@react-mdc/textfield'
import Button from '@react-mdc/button'
import Select from 'react-select'
import EmailExistsPrompt from '../containers/EmailExistsPrompt'
import { Errors } from 'hfty-components-errors'
import { Link } from 'react-router-dom'
import Warning from 'fc-icons/src/Warning'
import Tick from 'fc-icons/src/Tick'
import Cross from 'fc-icons/src/Cross'
import Eye from 'fc-icons/src/Eye'
import CloseEye from 'fc-icons/src/CloseEye'

// Utils
import traverse from 'fc-utils/traverse'

// Styles
import { error, primary, white, dark, lightRed } from 'hfty-styles/src/colors'
import '@material/dialog/dist/mdc.dialog.css'

// Image
import LOGO from 'hfty-images/src/FE_Soils_Logo_WhiteBg.png'

// Constants
import {
  PROP_FARM_NAME,
  PROP_USERNAME,
  PROP_FIRST_NAME,
  PROP_LAST_NAME,
  PROP_EMAIL,
  PROP_COUNTRY,
  PROP_ADDRESS,
  PROP_POSTAL_CODE,
  PROP_CITY,
  PROP_PROVINCE,
  PROP_PHONE,
  PROP_MOBILE_PHONE,
  PROP_FAX,
  PROP_DEFAULT_UNITS,
  PROP_PASSWORD,
  PROP_PASSWORD_CONFIRM,
  PROP_AREA,
} from 'hfty-model-accounts'
import { PROP_US_CUSTOMARY, PROP_METRIC } from 'fc-model-users'
import { COUNTRIES_OPTS, US_STATES_OPTS } from 'hfty'
import { DEFAULT_UNITS_OPTS, PASSWORD_VALIDATION, PASSWORD_VALIDATION_REGEX, PASSWORD_LENGTH } from '../constants'

// Styles
import '../styles.scss'

const styles = {
  logo: (mobile: boolean): Object => ({
    position: mobile ? 'static' : 'absolute',
    left: '-400px',
    top: '63px',
    display: 'block',
    textAlign: 'center',
    padding: mobile ? '7px 0' : '0',
  }),
  midlogo: {
    position: 'absolute',
    left: '-280px',
    top: '63px',
    display: 'block',
    textAlign: 'center',
    padding: '0',
  },
  heading: {
    color: primary(),
    fontSize: '1.75em',
    fontWeight: 600,
    margin: '0 0 15px',
    textAlign: 'center',
  },
  subHeading: {
    margin: '35px 0px 5px',
    textAlign: 'center',
  },
  container: {
    backgroundColor: white(),
    padding: '40px 0',
  },
  errorBox: {
    margin: '15px 0',
  },
  innerContainer: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    alignItems: 'center',
    backgroundColor: white(),
    padding: '0 35px',
    maxWidth: '360px',
    margin: '0 auto',
    boxSizing: 'border-box',
  },
  inputWrapper: {
    width: '100%',
    fontSize: '15px',
  },
  outerContainer: {
    backgroundColor: primary(),
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    overflow: 'auto',
    color: dark(),
  },
  mobileOuterContainer: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    color: dark(),
    backgroundColor: primary(),
  },
  wrapper: (mobile: boolean): Object => ({
    position: 'relative',
    width: mobile ? '100%' : '40%',
  }),
  logoStyles: {
    width: '250px',
  },
  label: {
    color: 'var(--mdc-theme-text-hint-on-light,rgba(0,0,0,.87))',
    marginRight: 15,
    fontFamily: 'Robot,sans-serif',
    fontSize: '1rem',
    marginTop: 20,
    letterSpacing: '0.04em',
  },
  selectWrapper: {
    margin: '1em 0 .5em',
    width: '100%',
  },
  selectLabel: {
    display: 'inline-block',
    fontSize: '.75em',
    marginBottom: '.65em',
    color: 'var(--mdc-theme-text-hint-on-light,rgba(0,0,0,.87))',
  },
  input: {
    color: dark(),
  },
  passwordError: {
    color: error(),
  },
  successMsg: {
    backgroundColor: 'rgba(131, 181, 64, 0.3)',
    padding: 20,
    textAlign: 'center',
    margin: 20,
  },
  btnActions: {
    marginTop: '35px',
  },
  footnote: {
    color: primary(),
    fontSize: 14,
    margin: 20,
    textAlign: 'center',
  },
  cancelLink: {
    textDecoration: 'none',
    color: white(),
  },
  cancelBtn: {
    marginLeft: '15px',
    backgroundColor: primary(),
  },
  caseSensitiveText: {
    fontSize: '12px',
    padding: '3px',
    fontWeight: 500,
  },

  passwordPopupWrapper: {
    height: '180px',
    backgroundColor: 'white',
    border: `1px solid ${error()}`,
    zIndex: 1000,
    position: 'absolute',
  },
  passwordPopupHeader: {
    borderBottom: `1px solid ${error()}`,
    padding: '10px',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    fontWeight: 'bold',
    colGap: '10px',
    backgroundColor: lightRed(),
    color: error(),
  },
  passwordCheckListItem: {
    width: '200px',
    display: 'flex',
    alignItems: 'center',
    height: '30px',
  },
  passwordPopupBody: {
    fontSize: '12px',
    display: 'flex',
    justifyContent: 'center',
    padding: 10,
  },
  passwordCheckList: {
    display: 'flex',
    alignItems: 'align-start',
    justifyContent: 'start',
    flexDirection: 'column',
  },
}

// Local Types
type Props = {
  operation: AccountOperation,
  onChange: Function,
  onSubmit: Function,
  actions: {
    onChange: Function,
    onSubmit: Function,
  },
  viewport: {
    width: number,
  },
}
type State = {
  emailInUseDialogOpen: boolean,
  unitsMenuOpen: boolean,
  passwordError: ?string,
}

const getFarmLabel = traverse(['operation', 'model', PROP_FARM_NAME], { default: '' })
const getFirstName = traverse(['operation', 'model', PROP_FIRST_NAME], { default: '' })
const getLastName = traverse(['operation', 'model', PROP_LAST_NAME], { default: '' })
const getEmail = traverse(['operation', 'model', PROP_EMAIL], { default: '' })
const getCountry = traverse(['operation', 'model', PROP_COUNTRY], { default: 'United States' })
const getAddress = traverse(['operation', 'model', PROP_ADDRESS], { default: '' })
const getCity = traverse(['operation', 'model', PROP_CITY], { default: '' })
const getProvince = traverse(['operation', 'model', PROP_PROVINCE], { default: '' })
const getPostalCode = traverse(['operation', 'model', PROP_POSTAL_CODE], { default: '' })
const getPhone = traverse(['operation', 'model', PROP_PHONE], { default: '' })
const getDefaultUnits = traverse(['operation', 'model', PROP_DEFAULT_UNITS], { default: PROP_US_CUSTOMARY })
const getUsername = traverse(['operation', 'model', PROP_USERNAME], { default: '' })
const getPassword = traverse(['operation', 'model', PROP_PASSWORD], { default: '' })
const getPasswordConfirm = traverse(['operation', 'model', PROP_PASSWORD_CONFIRM], { default: '' })
const getArea = traverse(['operation', 'model', PROP_AREA], { default: '' })

export default class AddAccount extends Component {
  props: Props
  state: State
  onChange: Function
  onChangePassword: Function
  onSubmit: Function

  constructor(props: Props) {
    super(props)

    this.state = {
      emailInUseDialogOpen: false,
      unitsMenuOpen: false,
      passwordError: null,
      showPasswordErrMode: false,
      showConfirmPasswordErrMode: false,
      showPassword: false,
      showConfirmPassword: false,
    }

    this.closeDialog = this.closeDialog.bind(this)
    this.onChange = this.onChange.bind(this)
    this.onSubmit = this.onSubmit.bind(this)
    this.renderPasswordCheck = this.renderPasswordCheck.bind(this)
  }

  componentWillMount() {
    _delay(
      function (props: Props) {
        props.actions.onChange({
          ...props.operation,
          model: {
            ...props.operation.model,
            [PROP_COUNTRY]: getCountry(props),
            [PROP_DEFAULT_UNITS]: getDefaultUnits(props),
            [PROP_MOBILE_PHONE]: '',
            [PROP_FAX]: '',
          },
        })
      },
      1000,
      this.props
    )
  }

  componentWillReceiveProps(nextProps: Props) {
    const hasErrors = !!this.props.operation.errors
    if (hasErrors && _size(this.props.operation.errors) > 0) {
      const errorBoxes = document.getElementsByClassName('errorBox')
      if (_size(errorBoxes) > 0) {
        errorBoxes[0].scrollIntoView()
      }
      const errorMsg = this.props.operation.errors[0].error
      if (errorMsg === 'userWithEmailAlreadyExists') {
        this.setState({ emailInUseDialogOpen: true })
      }
    }

    if (this.props.operation.success) {
      document.getElementById('successMsgBox').scrollIntoView()
    }
  }

  closeDialog() {
    this.setState({ emailInUseDialogOpen: false })
  }

  onChange(value: any, modelProp: string) {
    if (modelProp === PROP_EMAIL) {
      this.props.actions.onChange({
        ...this.props.operation,
        model: {
          ...this.props.operation.model,
          [modelProp]: _trim(value),
        },
      })
    } else if (modelProp === PROP_COUNTRY) {
      let default_units = _toUpper(PROP_US_CUSTOMARY)
      if (value === 'Canada') {
        default_units = _toUpper(PROP_METRIC)
      }
      this.props.actions.onChange({
        ...this.props.operation,
        model: {
          ...this.props.operation.model,
          [PROP_COUNTRY]: value,
          [PROP_DEFAULT_UNITS]: default_units,
        },
      })
    } else {
      this.props.actions.onChange({
        ...this.props.operation,
        model: {
          ...this.props.operation.model,
          [modelProp]: value,
        },
      })
    }
  }

  onSubmit(e: Event & { currentTarget: HTMLInputElement }) {
    e.preventDefault()
    this.props.actions.onSubmit()
  }

  renderFirstName(): React.Element<any> {
    const error = this.getError(PROP_FIRST_NAME)
    return (
      <div style={styles.inputWrapper}>
        <Textfield style={styles.inputWrapper}>
          <Textfield.Input
            id={`firstName-${this.props.operation.operationId}`}
            onChange={(e: Event) => {
              this.onChange(traverse(['target', 'value'])(e), PROP_FIRST_NAME)
            }}
            value={getFirstName(this.props)}
            required
            style={styles.input}
          />
          <Textfield.Label htmlFor={`firstName-${this.props.operation.operationId}`}>
            <FormattedMessage {...messages['rgstrtn.first_name']} />
          </Textfield.Label>
        </Textfield>
        {this.renderError(error)}
      </div>
    )
  }

  renderLastName(): React.Element<any> {
    const error = this.getError(PROP_LAST_NAME)
    return (
      <div style={styles.inputWrapper}>
        <Textfield style={styles.inputWrapper}>
          <Textfield.Input
            id={`lastName-${this.props.operation.operationId}`}
            onChange={(e: Event) => {
              this.onChange(traverse(['target', 'value'])(e), PROP_LAST_NAME)
            }}
            value={getLastName(this.props)}
            required
            style={styles.input}
          />
          <Textfield.Label htmlFor={`lastName-${this.props.operation.operationId}`}>
            <FormattedMessage {...messages['rgstrtn.last_name']} />
          </Textfield.Label>
        </Textfield>
        {this.renderError(error)}
      </div>
    )
  }

  renderFarmName(): React.Element<any> {
    const error = this.getError(PROP_FARM_NAME)
    return (
      <div style={styles.inputWrapper}>
        <Textfield style={styles.inputWrapper}>
          <Textfield.Input
            id={`farmlabel-${this.props.operation.operationId}`}
            onChange={(e: Event) => {
              this.onChange(traverse(['target', 'value'])(e), PROP_FARM_NAME)
            }}
            value={getFarmLabel(this.props)}
            required
            style={styles.input}
          />
          <Textfield.Label htmlFor={`farmlabel-${this.props.operation.operationId}`}>
            <FormattedMessage {...messages['rgstrtn.farm_name']} />
          </Textfield.Label>
        </Textfield>
        {this.renderError(error)}
      </div>
    )
  }

  renderEmail(): React.Element<any> {
    const error = this.getError(PROP_EMAIL)
    return (
      <div style={styles.inputWrapper}>
        <Textfield style={styles.inputWrapper}>
          <Textfield.Input
            id={`email-${this.props.operation.operationId}`}
            onChange={(e: Event) => {
              this.onChange(traverse(['target', 'value'])(e), PROP_EMAIL)
            }}
            value={getEmail(this.props)}
            required
            style={styles.input}
          />
          <Textfield.Label htmlFor={`email-${this.props.operation.operationId}`}>
            <FormattedMessage {...messages['rgstrtn.email']} />
          </Textfield.Label>
        </Textfield>
        {this.renderError(error)}
      </div>
    )
  }

  renderCountry(): React.Element<any> {
    const error = this.getError(PROP_COUNTRY)
    return (
      <div style={styles.selectWrapper}>
        <label style={styles.selectLabel}>
          <FormattedMessage {...messages['rgstrtn.country']} />*
        </label>
        <Select
          clearable={false}
          placeholder="Select country"
          name={`country-${this.props.operation.operationId}`}
          value={getCountry(this.props)}
          options={COUNTRIES_OPTS}
          style={styles.select}
          onChange={(option: Option) => {
            this.onChange(option.value, PROP_COUNTRY)
          }}
        />
        {this.renderError(error)}
      </div>
    )
  }

  renderAddress(): React.Element<any> {
    const error = this.getError(PROP_ADDRESS)
    return (
      <div style={styles.inputWrapper}>
        <Textfield style={styles.inputWrapper}>
          <Textfield.Input
            id={`address-${this.props.operation.operationId}`}
            onChange={(e: Event) => {
              this.onChange(traverse(['target', 'value'])(e), PROP_ADDRESS)
            }}
            value={getAddress(this.props)}
            required
            style={styles.input}
          />
          <Textfield.Label htmlFor={`address-${this.props.operation.operationId}`}>
            <FormattedMessage {...messages['rgstrtn.address']} />
          </Textfield.Label>
        </Textfield>
        {this.renderError(error)}
      </div>
    )
  }

  renderCity(): React.Element<any> {
    const error = this.getError(PROP_CITY)
    return (
      <div style={styles.inputWrapper}>
        <Textfield style={styles.inputWrapper}>
          <Textfield.Input
            id={`city-${this.props.operation.operationId}`}
            onChange={(e: Event) => {
              this.onChange(traverse(['target', 'value'])(e), PROP_CITY)
            }}
            value={getCity(this.props)}
            required
            style={styles.input}
          />
          <Textfield.Label htmlFor={`city-${this.props.operation.operationId}`}>
            <FormattedMessage {...messages['rgstrtn.city']} />
          </Textfield.Label>
        </Textfield>
        {this.renderError(error)}
      </div>
    )
  }

  renderProvince(): React.Element<any> {
    const error = this.getError(PROP_PROVINCE)
    const country = getCountry(this.props)
    const addressStateLabel = country === 'Canada' ? 'rgstrtn.province' : 'rgstrtn.state'
    return (
      <div style={styles.selectWrapper}>
        <label style={styles.selectLabel} htmlFor={`state-${this.props.operation.operationId}`}>
          <FormattedMessage {...messages[addressStateLabel]} />*
        </label>
        <Select
          clearable={false}
          name={`state-${this.props.operation.operationId}`}
          placeholder="Select state"
          options={US_STATES_OPTS}
          style={styles.select}
          value={getProvince(this.props)}
          onChange={(option: Option) => {
            this.onChange(option.value, PROP_PROVINCE)
          }}
        />
        {this.renderError(error)}
      </div>
    )
  }

  renderPostalCode(): React.Element<any> {
    const error = this.getError(PROP_POSTAL_CODE)
    const country = getCountry(this.props)
    const addressCodeLabel = country === 'Canada' ? 'rgstrtn.postal_code' : 'rgstrtn.zip_code'
    return (
      <div style={styles.inputWrapper}>
        <Textfield style={styles.inputWrapper}>
          <Textfield.Input
            id={`postalCode-${this.props.operation.operationId}`}
            onChange={(e: Event) => {
              this.onChange(traverse(['target', 'value'])(e), PROP_POSTAL_CODE)
            }}
            value={getPostalCode(this.props)}
            required
            style={styles.input}
          />
          <Textfield.Label htmlFor={`postalCode-${this.props.operation.operationId}`}>
            <FormattedMessage {...messages[addressCodeLabel]} />
          </Textfield.Label>
        </Textfield>
        {this.renderError(error)}
      </div>
    )
  }

  renderPhone(): React.Element<any> {
    const error = this.getError(PROP_PHONE)

    return (
      <div style={styles.inputWrapper}>
        <Textfield style={styles.inputWrapper}>
          <Textfield.Input
            id={`phone-${this.props.operation.operationId}`}
            onChange={(e: Event) => {
              this.onChange(traverse(['target', 'value'])(e), PROP_PHONE)
            }}
            value={getPhone(this.props)}
            required
            style={styles.input}
          />
          <Textfield.Label htmlFor={`phone-${this.props.operation.operationId}`}>
            <FormattedMessage {...messages['rgstrtn.phone']} />
          </Textfield.Label>
        </Textfield>
        {this.renderError(error)}
      </div>
    )
  }

  renderDefaultUnit(): React.Element<any> {
    const error = this.getError(PROP_DEFAULT_UNITS)

    return (
      <div style={styles.selectWrapper}>
        <label style={styles.selectLabel} htmlFor={`default-units-${this.props.operation.operationId}`}>
          <FormattedMessage {...messages['rgstrtn.default_units']} />*
        </label>
        <Select
          clearable={false}
          name={`default-units-${this.props.operation.operationId}`}
          placeholder="Select default units"
          options={DEFAULT_UNITS_OPTS}
          style={styles.select}
          value={getDefaultUnits(this.props)}
          onChange={(option: Option) => {
            this.onChange(option.value, PROP_DEFAULT_UNITS)
          }}
        />
        {this.renderError(error)}
      </div>
    )
  }

  renderUsername(): React.Element<any> {
    const error = this.getError(PROP_USERNAME)

    return (
      <div style={styles.inputWrapper}>
        <Textfield style={styles.inputWrapper}>
          <Textfield.Input
            id={`username-${this.props.operation.operationId}`}
            onChange={(e: Event) => {
              this.onChange(traverse(['target', 'value'])(e), PROP_USERNAME)
            }}
            value={getUsername(this.props)}
            required
            style={styles.input}
          />
          <Textfield.Label htmlFor={`username-${this.props.operation.operationId}`}>
            <FormattedMessage {...messages['rgstrtn.username']} />
          </Textfield.Label>
        </Textfield>
        {this.renderError(error)}
      </div>
    )
  }

  renderPassword(): React.Element<any> {
    const error = this.getError(PROP_PASSWORD)

    return (
      <div style={styles.inputWrapper}>
        <Textfield style={styles.inputWrapper}>
          <Textfield.Input
            type={this.state.showPassword ? 'text' : 'password'}
            id={`password-${this.props.operation.operationId}`}
            onChange={(e: Event) => {
              this.onChange(traverse(['target', 'value'])(e), PROP_PASSWORD)
              this.setState({ showPasswordErrMode: true })
            }}
            value={getPassword(this.props)}
            required
            style={styles.input}
          />
          <Textfield.Label htmlFor={`password-${this.props.operation.operationId}`}>
            <FormattedMessage {...messages['Password']} />
          </Textfield.Label>
          <span onClick={() => this.setState({ showPassword: !this.state.showPassword })} style={{ cursor: 'pointer' }}>
            {this.state.showPassword ? <CloseEye /> : <Eye />}
          </span>
        </Textfield>
        {this.state.showPasswordErrMode && this.renderPasswordCheck(getPassword(this.props), false)}
      </div>
    )
  }

  renderPasswordCheck(password, isConfirmPassword) {
    let PASSWORD_VALIDATION_ERR_CHECKS = {}

    const containsPersonalInfo =
      new RegExp(this.props.operation.model.first).test(password) ||
      new RegExp(this.props.operation.model.last).test(password) ||
      new RegExp(this.props.operation.model.username).test(password) ||
      new RegExp(this.props.operation.model.email).test(password)

    if (password) {
      PASSWORD_VALIDATION_ERR_CHECKS = {
        length: password.length >= PASSWORD_LENGTH,
        specialChar: password.match(PASSWORD_VALIDATION_REGEX.specialChar) ? true : false,
        uppercase: password.match(PASSWORD_VALIDATION_REGEX.uppercase) ? true : false,
        containsPersonalInfo: !containsPersonalInfo,
        number: password.match(PASSWORD_VALIDATION_REGEX.number) ? true : false,
        lowercase: password.match(PASSWORD_VALIDATION_REGEX.lowercase) ? true : false,
        identicalChars: !/(.)\1\1+/.test(password),
      }
    } else {
      PASSWORD_VALIDATION_ERR_CHECKS = {
        length: false,
        specialChar: false,
        uppercase: false,
        containsPersonalInfo: false,
        number: false,
        lowercase: false,
        identicalChars: false,
      }
    }

    if (isConfirmPassword) {
      PASSWORD_VALIDATION_ERR_CHECKS['passwordMatch'] = this.props.operation.model.password === password ? true : false
    }

    if (Object.keys(PASSWORD_VALIDATION_ERR_CHECKS).length > 4 && password.length >= 8) {
      const allPasswordChecksPassed = Object.keys(PASSWORD_VALIDATION_ERR_CHECKS).find(
        (key) => PASSWORD_VALIDATION_ERR_CHECKS[key] === false
      )

      if (allPasswordChecksPassed === undefined) {
        if (isConfirmPassword && this.state.showConfirmPasswordErrMode) {
          this.setState({ showConfirmPasswordErrMode: false })
        } else if (this.state.showPasswordErrMode) {
          this.setState({ showPasswordErrMode: false })
        }
      }
    }
    return (
      <div style={styles.passwordPopupWrapper}>
        <div style={styles.passwordPopupHeader}>
          <Warning />
          <span style={{ paddingLeft: '15px' }}>
            <FormattedMessage {...messages['passwordCheckListHeader']} />
          </span>
        </div>
        <div style={styles.passwordPopupBody}>
          {[0, 1].map((group) => (
            <div key={group}>
              {Object.keys(PASSWORD_VALIDATION_ERR_CHECKS).map((key, index) => (
                <div key={key}>
                  {(group === 0 ? index < 4 : index > 3) && (
                    <div style={styles.passwordCheckListItem}>
                      <div>
                        {PASSWORD_VALIDATION_ERR_CHECKS[key] ? (
                          <Tick style={{ paddingRight: '5px' }} />
                        ) : (
                          <Cross style={{ paddingRight: '5px' }} />
                        )}
                      </div>
                      <p style={{ paddingLeft: '5px' }}>{PASSWORD_VALIDATION[key]}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    )
  }

  renderConfirmPassword(): React.Element<any> {
    const error = this.getError(PROP_PASSWORD_CONFIRM)

    return (
      <div style={styles.inputWrapper}>
        <Textfield style={styles.inputWrapper}>
          <Textfield.Input
            type={this.state.showConfirmPassword ? 'text' : 'password'}
            id={`confirmPassword-${this.props.operation.operationId}`}
            onChange={(e: Event) => {
              this.onChange(traverse(['target', 'value'])(e), PROP_PASSWORD_CONFIRM)
              this.setState({ showConfirmPasswordErrMode: true })
            }}
            value={getPasswordConfirm(this.props)}
            required
            style={styles.input}
          />
          <Textfield.Label htmlFor={`confirmPassword-${this.props.operation.operationId}`}>
            <FormattedMessage {...messages['rgstrtn.confirm_password']} />
          </Textfield.Label>
          <span
            onClick={() => this.setState({ showConfirmPassword: !this.state.showConfirmPassword })}
            style={{ cursor: 'pointer' }}
          >
            {this.state.showConfirmPassword ? <CloseEye /> : <Eye />}
          </span>
        </Textfield>
        {this.state.showConfirmPasswordErrMode && this.renderPasswordCheck(getPasswordConfirm(this.props), true)}
      </div>
    )
  }

  renderArea(): React.Element<any> {
    const error = this.getError(PROP_AREA)
    const units = getDefaultUnits(this.props)
    let areaUnit = 'acres'
    if (units === 'METRIC') {
      areaUnit = 'hectares'
    }
    return (
      <div style={styles.inputWrapper}>
        <Textfield style={styles.inputWrapper}>
          <Textfield.Input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            id={`area-${this.props.operation.operationId}`}
            onChange={(e: Event) => {
              const value = traverse(['target', 'value'])(e)
              this.onChange(value.replace(/[^0-9]/g, ''), PROP_AREA)
            }}
            value={getArea(this.props)}
            style={styles.input}
          />
          <Textfield.Label htmlFor={`area-${this.props.operation.operationId}`}>
            How many {areaUnit} will you test this season?
          </Textfield.Label>
        </Textfield>
        {this.renderError(error)}
      </div>
    )
  }

  getError(modelProp: string): ?Object {
    const { operation } = this.props
    const errors = operation.errors || []
    return _find(errors, (error: Object): boolean => error.key === modelProp) || null
  }

  renderError(error: Object): React.Element<any> {
    const { operation } = this.props
    const { success, triedSubmission } = operation
    return (
      !success &&
      triedSubmission &&
      error && (
        <div className={'errorBox'} style={styles.errorBox}>
          <Errors
            errors={!success && triedSubmission && error ? [_pick(error, ['error'])] : null}
            messages={messages}
          />
        </div>
      )
    )
  }

  renderUsernameisCaseSentiveText(): React.Element<any> {
    return (
      <div style={styles.caseSensitiveText}>
        <FormattedMessage {...messages['usernameIsCaseSensitive']} />
      </div>
    )
  }

  render(): ?React.Element<any> {
    const mobile = this.props.viewport.width < 905
    const midScreen = this.props.viewport.width < 1300 && this.props.viewport.width > 905
    const success = this.props.operation.success
    const successMsg = <FormattedMessage {...messages['rgstrtn.user_creation_success']} />
    const isSubmitDisabled = !!this.props.operation.errors && _size(this.props.operation.errors) > 0

    return (
      <div style={mobile ? styles.mobileOuterContainer : styles.outerContainer} className="registration">
        <div style={styles.wrapper(mobile)}>
          <Link to="/" style={midScreen ? styles.midlogo : styles.logo(mobile)}>
            <img src={LOGO} style={styles.logoStyles} />
          </Link>
          <div style={styles.container}>
            <div style={styles.heading}>
              <FormattedMessage {...messages['rgstrtn.sign_up_now']} />
            </div>
            <form style={styles.innerContainer} onSubmit={this.onSubmit}>
              <h4 style={styles.subHeading}>
                <FormattedMessage {...messages['rgstrtn.required_information']} />
              </h4>
              {this.renderFirstName()}
              {this.renderLastName()}
              {this.renderFarmName()}
              {this.renderEmail()}
              {this.renderCountry()}
              {this.renderAddress()}
              {this.renderCity()}
              {this.renderProvince()}
              {this.renderPostalCode()}
              {this.renderPhone()}
              {this.renderDefaultUnit()}
              <h4 style={styles.subHeading}>
                <FormattedMessage {...messages['rgstrtn.create_username_password']} />
              </h4>
              {this.renderUsername()}
              {this.renderUsernameisCaseSentiveText()}
              {this.renderPassword()}
              {this.renderConfirmPassword()}
              <h4 style={styles.subHeading}>
                <FormattedMessage {...messages['rgstrtn.other_details_optional']} />
              </h4>
              {this.renderArea()}
              <div style={styles.btnActions}>
                <Button raised primary onClick={this.onSubmit} disabled={isSubmitDisabled}>
                  <FormattedMessage {...messages['rgstrtn.submit']} />
                </Button>
                <Button raised style={styles.cancelBtn}>
                  <Link to="/" style={styles.cancelLink}>
                    Cancel
                  </Link>
                </Button>
              </div>
              <div id="successMsgBox" style={success ? styles.successMsg : {}}>
                {success ? successMsg : null}
              </div>
              <div style={styles.footnote}>
                <FormattedMessage {...messages['rgstrtn.reminder_order']} />
              </div>
            </form>
            <EmailExistsPrompt
              open={this.state.emailInUseDialogOpen}
              viewport={this.props.viewport}
              onClose={this.closeDialog}
            />
          </div>
        </div>
      </div>
    )
  }
}
